import {CookingActivity, CookingActivityStep} from "./CookingActivity";
import {KitchenScreen} from "../../States/KitchenScreen";
import {array, Callback} from "../../Core/Reflection";
import {Sprite} from "../../Core/Sprite";
import {coconutStep1, coconutStep2, coconutStep3, coconutStep4, coconutStep5} from "../../Layout/KitchenScreenLayout";
import {Ease} from "../../Core/Animatable";
import {CookingItem} from "./CookingItem";
import {Container} from "../../Core/Container";
import {Signal, Times} from "../../Core/Signal";
import * as PIXI from "pixi.js";
import {IPoint, PointUtil} from "../../Core/Geom";
import {Poly} from "../../Core/Shape";
import {AudioChannel} from "../../Core/AudioChannel";
import {Keyboard} from "../Keyboard";
import {Pouring} from "./Ingredient";
import {playVO, ReactionVO, voOptionsFacts} from "../../Data/VoiceOver";
import {ArrayUtils} from "html-living-framework";

export class CoconutMilkActivity extends CookingActivity {

    constructor(kitchen: KitchenScreen) {
        super(kitchen, [
            CoconutMilkActivityStep1_Cut,
            CoconutMilkActivityStep2_Glass,
            CoconutMilkActivityStep3_Meat,
            CoconutMilkActivityStep4_Fiber,
            CoconutMilkActivityStep5_Drink,
        ]);
    }
}

class CoconutMilkActivityStep1_Cut extends CookingActivityStep {

    readonly cuttingBoard = this.addChild(new Sprite());
    readonly coconutCut = this.addChild(new (class extends CookingItem {
        readonly bottom = array(it => new Sprite());
        readonly top = array(it => new Sprite());
    })());

    play(onComplete: Callback, prev?: CookingActivityStep) {
        coconutStep1.call(this);

        this.moveItemsFromLeft(this.cuttingBoard, undefined, 0.3);
        const coconut = this.coconutCut;
        coconut.draggable = false;
        coconut.enabled = false;
        this.moveItemsFromLeft(coconut, () => {
            playVO(["LetsGoLuna_182", "LetsGoLuna_183"], () => {
                if (coconut.enabled) {
                    coconut.pulse(undefined, undefined, true);
                }
            });
            coconut.enabled = true;
        }, 0.3);
        coconut.onClick.add(() => {
            AudioChannel.SFX.play("knife-slice");
            coconut.stopPulse();
            coconut.enabled = false;
            coconut.sprite.visible = false;

            const top = coconut.top;
            const bottom = coconut.bottom;
            coconut.visuals.addChild(bottom[0]);
            coconut.visuals.addChild(top[0]);

            const onCut = () => {
                top[0].changeParent(this);
                this.moveItemsToLeft(top[0], () => {
                    coconut.moveYTo(coconut.y + this.cuttingBoard.height * 0.1, 0.05, Ease.sineIn, {delay: 0.1});
                    this.moveItemsToRight(this.cuttingBoard, onComplete);
                });
            }

            for (const it of [top, bottom]) {
                it[0].tweenTo(0.3, Ease.sineIn, {
                    x: it[1].x,
                    y: it[1].y,
                    scaleX: it[1].scale.x,
                    scaleY: it[1].scale.y,
                    rotation: it[1].rotation,
                    onComplete: () => {
                        it[0].tweenTo(0.3, Ease.sineOut, {
                            x: it[2].x,
                            y: it[2].y,
                            scaleX: it[2].scale.x,
                            scaleY: it[2].scale.y,
                            rotation: it[2].rotation,
                            onComplete: it === top ? onCut : undefined
                        });
                    }
                });
            }
            Keyboard.focus(this.kitchen.buttonHome);
        });
        Keyboard.add(coconut);
        Keyboard.focus(coconut);
    }
}

class CoconutMilkActivityStep2_Glass extends CookingActivityStep {

    readonly coconutHalf = this.addChild(new Coconut());
    readonly glass = this.addChild(new Glass());

    play(onComplete: Callback, prev: CoconutMilkActivityStep1_Cut) {
        coconutStep2.call(this);
        playVO("LetsGoLuna_193", voOptionsFacts);

        const glass = this.glass;
        const coconut = this.coconutHalf;

        coconut.enabled = false;
        coconut.saveTransform();
        coconut.tweenFrom(0.6, Ease.sineInOut, {
            x: prev.coconutCut.x,
            y: prev.coconutCut.y,
            scaleX: prev.coconutCut.scale.x,
            scaleY: prev.coconutCut.scale.y,
        });
        this.moveItemsFromRight(this.glass, () => {
            glass.enabled = true;
            coconut.enabled = true;

            coconut.pulse();
            coconut.target = glass;
            coconut.targetTransform = {
                x: glass.x - glass.width * 0.5,
                y: glass.y - glass.height,
                rotation: 80 * Math.PI / 180
            };
            coconut.targetDistance = glass.height;

            coconut.onStartDrag.add(() => coconut.stopPulse());
            coconut.onDragged.add(() => {
                coconut.enabled = false;
                glass.enabled = false;
                const onCompletePour = Times(2, () => {
                    coconut.tweenTo(0.4, Ease.sineInOut, {
                        ...coconut.getTransformBackup(),
                        onComplete: onComplete
                    });
                });
                playVO("LetsGoLuna_184");
                AudioChannel.SFX.play("liquid-pour");
                coconut.pour(onCompletePour);
                glass.delayedCall(0.6, () => glass.showDrink(onCompletePour));
            });
            coconut.onKeyboard.add(() => {
                coconut.stopPulse();
                Keyboard.focus(this.kitchen.buttonHome);
            });
            Keyboard.add(coconut);
            Keyboard.focus(coconut);
        }, 0.2);
        prev.destroy();
    }
}

class CoconutMilkActivityStep3_Meat extends CookingActivityStep {

    readonly coconutHalf = this.addChild(new Coconut());
    readonly glass = this.addChild(new Glass());
    readonly bowl = this.addChild(new Bowl());
    readonly coconutMeat = new CoconutMeat();

    play(onComplete: Callback, prev: CoconutMilkActivityStep2_Glass) {
        coconutStep3.call(this);

        const glass = this.glass;
        const bowl = this.bowl;
        const coconut = this.coconutHalf;
        const meat = this.coconutMeat;

        coconut.draggable = false;
        coconut.enabled = false;
        coconut.saveTransform();
        coconut.moveXYFrom(prev.coconutHalf.x, prev.coconutHalf.y, 0.6, Ease.sineInOut);
        glass.showDrink(true);
        glass.moveXYFrom(prev.glass.x, prev.glass.y, 0.6, Ease.sineInOut);
        this.moveItemsFromRight(this.bowl, () => {
            coconut.enabled = true;
            coconut.target = bowl;
            coconut.pulse();
            const startDragMeat = (p: IPoint) => {
                coconut.stopPulse();
                coconut.setEmpty();
                this.addChild(meat);
                meat.startDrag(p);
            };
            coconut.onStartDrag.add((e: PIXI.InteractionEvent) => {
                startDragMeat(e.data.getLocalPosition(this));
            });
            coconut.onVirtualDrag.add(data => {
                const p = data.getLocalPosition(this);
                meat.x = p.x;
                meat.y = p.y;
            });
            coconut.onDragged.add(() => {
                coconut.enabled = false;
                const s = 1000;
                const p = {x: bowl.x, y: bowl.y - bowl.height * 0.7};
                AudioChannel.SFX.play("knife-slice");
                playVO("LetsGoLuna_185");
                meat.moveXYTo(p.x, p.y, PointUtil.distance(coconut, p) / s, Ease.sineInOut, () => {
                    meat.changeParent(bowl.visuals);
                    meat.visible = false;
                    bowl.eat(0);
                    bowl.coconutMeat[0].tweenFrom(0.4, Ease.sineInOut, {
                        x: meat.x,
                        y: meat.y,
                        scaleX: meat.scale.x,
                        scaleY: meat.scale.y,
                        onComplete: onComplete
                    });
                });
            });
            coconut.onKeyboard.add(() => {
                startDragMeat(coconut);
                Keyboard.focus(this.kitchen.buttonHome);
            });
            Keyboard.add(coconut);
            Keyboard.focus(coconut);
        }, 0.2);
        prev.destroy();
    }
}

class CoconutMilkActivityStep4_Fiber extends CookingActivityStep {

    readonly coconutHalf = this.addChild(new Coconut());
    readonly glass = this.addChild(new Glass());
    readonly bowl = this.addChild(new Bowl());
    readonly fiber = this.addChild(new CookingItem());

    play(onComplete: Callback, prev: CoconutMilkActivityStep3_Meat) {
        coconutStep4.call(this);

        const glass = this.glass;
        const bowl = this.bowl;
        const coconut = this.coconutHalf;
        const fiber = this.fiber;

        coconut.setEmpty(true);
        glass.showDrink(true);
        bowl.eat(0);
        fiber.visible = false;
        fiber.enabled = false;

        coconut.moveXYFrom(prev.coconutHalf.x, prev.coconutHalf.y, 0.6, Ease.sineInOut, () => {
            coconut.enabled = true;
            coconut.pulse();
            coconut.onClick.add(() => {
                AudioChannel.SFX.play("knife-slice");
                playVO("LetsGoLuna_186");
                coconut.stopPulse();
                coconut.enabled = false;
                coconut.alphaTo(0, 0.2);
                fiber.visible = true;
                fiber.alphaFrom(0, 0.2);
                fiber.moveXYFrom(coconut.x, coconut.y, 0.6, Ease.sineInOut, onComplete);
                Keyboard.focus(this.kitchen.buttonHome);
            });
            Keyboard.add(coconut);
            Keyboard.focus(coconut);
        });

        prev.destroy();
    }
}

class CoconutMilkActivityStep5_Drink extends CookingActivityStep {

    readonly glass = this.addChild(new Glass());
    readonly bowl = this.addChild(new Bowl());
    readonly fiber = this.addChild(new CookingItem());
    readonly rope = this.addChild(new Sprite());
    readonly straw = array(() => this.addChild(new CookingItem()));
    readonly strawTop = {x: 0, y: 0, rotation: 0};

    play(onComplete: Callback, prev: CoconutMilkActivityStep4_Fiber) {
        coconutStep5.call(this);

        const glass = this.glass;
        const bowl = this.bowl;
        const fiber = this.fiber;
        const rope = this.rope;
        const items = ["glass", "bowl", "fiber"] as ["glass", "bowl", "fiber"];

        glass.showDrink(true);
        bowl.eat(0);
        fiber.enabled = false;
        fiber.draggable = false;
        rope.visible = false;

        glass.hoverVO = "LetsGoLuna_184";
        bowl.hoverVO = "LetsGoLuna_185";
        fiber.hoverVO = "LetsGoLuna_186";

        let completeItems = 0;
        const onCompleteItem = (item: CookingItem) => {
            completeItems++;
            const vo =
                item === glass ? [...ReactionVO(), "LetsGoLuna_190"] :
                    item === bowl ? [...ReactionVO(), "LetsGoLuna_191"] :
                        item === fiber ? ["LetsGoLuna_192"] :
                            [ArrayUtils.random(["LetsGoLuna_188", "LetsGoLuna_189"])];
            if (completeItems >= 3) {
                playVO(vo, onComplete);
            } else {
                playVO(vo);
            }
        };

        const enableItems = () => {
            items.forEach(it => this[it].enabled = true);

            for (const straw of this.straw) {
                straw.enabled = true;
                straw.target = glass;
                straw.targetTransform = this.strawTop;
                straw.targetDistance = glass.height;
                straw.onStartDrag.add(() => {
                    this.straw.forEach(it => it.stopPulse());
                });
                straw.onCancel.add(() => pulseNext());
                straw.onDragged.add(() => {
                    this.straw.forEach(it => {
                        it.enabled = false;
                        it.stopPulse();
                    });
                    straw.changeParent(this.glass.visuals);
                    straw.visible = false;
                    glass.hoverVO = undefined;
                    glass.enabled = false;
                    glass.straw.visible = true;
                    glass.straw.texture = straw.sprite.texture;
                    glass.straw.tweenFrom(0.4, Ease.sineInOut, {
                        x: straw.x,
                        y: straw.y,
                        scaleX: straw.scale.x,
                        scaleY: straw.scale.y,
                        rotation: straw.rotation,
                        onComplete: () => {
                            this.moveItemsToLeft(this.straw.filter(it => it !== straw).reverse());
                            glass.hoverVO = "LetsGoLuna_184";
                            glass.enabled = true;
                            glass.onClick.remove();
                            glass.onClick.add(() => {
                                glass.enabled = false;
                                glass.hideDrink();
                                glass.stopPulse();
                                pulseNext();
                                onCompleteItem(glass);
                            });
                            pulseNext();
                        }
                    });
                });
                straw.onKeyboard.add(() => {
                    this.straw.forEach(it => it.stopPulse());
                    this.straw.forEach(it => it.enabled = false);
                    glass.enabled = false;
                    pulseNext();
                });
            }

            let index = 0;
            const eatingVO = ["LetsGoLuna_188", "LetsGoLuna_189"];
            ArrayUtils.shuffle(eatingVO);
            bowl.onClick.add(() => {
                index++;
                bowl.eat(index);
                AudioChannel.SFX.play("chewing");
                if (index >= 3) {
                    bowl.stopPulse();
                    bowl.enabled = false;
                    pulseNext();
                    onCompleteItem(bowl);
                } else {
                    const vo = eatingVO.pop();
                    if (vo) {
                        playVO([...ReactionVO(), vo]);
                    }
                }
            });

            fiber.onClick.add(() => {
                fiber.stopPulse();
                fiber.enabled = false;
                fiber.alphaTo(0, 0.2);
                rope.visible = true;
                rope.alphaFrom(0, 0.2, () => {
                    if (this.straw.every(it => it.enabled)) {
                        onCompleteItem(fiber);
                    } else {
                        rope.moveXTo(0, 0.6, Ease.sineInOut, () => {
                            onCompleteItem(fiber);
                        });
                    }
                });
                pulseNext();
            });

            Keyboard.add(...this.straw, glass, bowl, fiber);
            pulseNext();
        };

        const pulseNext = () => {
            if (this.straw.every(it => it.enabled)) {
                this.straw.forEach(it => it.pulse());
                Keyboard.focus(this.straw[this.straw.length - 1]);
            } else if (glass.enabled) {
                glass.pulse();
                Keyboard.focus(glass);
            } else if (bowl.enabled) {
                bowl.pulse();
                Keyboard.focus(bowl);
            } else if (fiber.enabled) {
                fiber.pulse();
                Keyboard.focus(fiber);
            } else {
                Keyboard.focus(this.kitchen.buttonHome);
            }
        };

        let d = 0;
        for (const name of items) {
            this[name].tweenFrom(0.6, Ease.sineInOut, {
                x: prev[name].x,
                y: prev[name].y,
                scaleX: prev[name].scale.x,
                scaleY: prev[name].scale.y,
                delay: d += 0.2
            });
        }
        this.straw.forEach(it => it.enabled = false);
        this.moveItemsFromLeft([...this.straw].reverse(), enableItems, 0.6);
        prev.destroy();
    }
}

class Glass extends CookingItem {

    readonly glassBack = this.visuals.addChild(new Sprite());
    readonly drinkBack = this.visuals.addChild(new Sprite());
    readonly straw = this.visuals.addChild(new Sprite());
    readonly drinkFront = this.visuals.addChild(new Sprite());
    readonly glassFront = this.visuals.addChild(new Sprite());
    readonly drinkMask: number[] = [];
    readonly strawBottom = {x: 0, y: 0, rotation: 0};

    constructor() {
        super();
        this.enabled = false;
        this.draggable = false;

        this.drinkBack.visible = false;
        this.drinkFront.visible = false;
        this.straw.visible = false;
    }

    showDrink(instantly?: boolean | Callback) {
        this.drinkBack.visible = true;
        this.drinkFront.visible = true;
        if (!instantly || typeof instantly === "function") {
            const mask = this.visuals.addChild(new Poly(this.drinkMask));
            const cb = Times(2, () => {
                mask.destroy();
                if (typeof instantly === "function") {
                    instantly();
                }
            });
            for (const drink of [this.drinkBack, this.drinkFront]) {
                drink.mask = mask;
                drink.moveYFrom(mask.bottom + drink.height * 0.5, 0.6, Ease.sineOut, () => {
                    drink.mask = null;
                    cb();
                });
            }
        }
    }

    hideDrink() {
        const mask = this.visuals.addChild(new Poly(this.drinkMask));
        const cb = Times(2, () => {
            mask.destroy();
        });
        for (const drink of [this.drinkBack, this.drinkFront]) {
            drink.mask = mask;
            drink.moveYTo(mask.bottom + drink.height * 0.5, 0.6, Ease.sineOut, () => {
                drink.mask = null;
                drink.visible = false;
                cb();
            });
        }
        this.straw.tweenTo(0.4, Ease.bounceOut, {
            x: this.strawBottom.x,
            y: this.strawBottom.y,
            rotation: this.strawBottom.rotation,
            delay: 0.2
        });
    }
}

class Coconut extends CookingItem {

    readonly pouringPoint = {x: 0, y: 0};
    readonly onVirtualDrag = Signal<(data: PIXI.InteractionData) => void>();
    readonly empty = this.visuals.addChild(new Sprite());

    constructor() {
        super();
        this.empty.visible = false;
    }

    setEmpty(instantly?: boolean) {
        this.empty.visible = true;
        if (instantly) {
            this.sprite.visible = false;
        } else {
            this.sprite.visible = false;
            const s = this.empty.scale.x;
            this.empty.tweenTo(0.1, Ease.sineIn, {
                scaleX: s * 1.2,
                scaleY: s * 0.8,
                onComplete: () => this.empty.tweenTo(0.5, Ease.backOut, {scaleX: s, scaleY: s})
            });
        }
    }

    pour(cb?: Callback) {
        const pouring = this.visuals.addChild(new Pouring());
        pouring.x = this.pouringPoint.x;
        pouring.y = this.pouringPoint.y;
        pouring.rotation = -this.rotation;
        pouring.emitter.playOnce(cb);
        return pouring;
    }
}

class Bowl extends CookingItem {

    readonly bowlBack = this.visuals.addChild(new Sprite());
    readonly bowlContent = this.visuals.addChild(new Container());
    readonly bowlFront = this.visuals.addChild(new Sprite());
    readonly coconutMeat = array(() => this.bowlContent.addChild(new Sprite()));

    constructor() {
        super();
        this.enabled = false;
        this.draggable = false;
    }

    onLayout() {
        this.coconutMeat.forEach((it, i) => it.visible = false);
    }

    eat(index: number) {
        this.coconutMeat.forEach((it, i) => it.visible = i === index);
    }
}

class CoconutMeat extends Sprite {

    startDrag(p: IPoint) {
        this.x = p.x;
        this.y = p.y;
        this.alpha = 1;
        this.alphaFrom(0, 0.2);
    }
}